import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import FormDialog from "common/Modal/Modal";
import {
  getCachedMediaObjectUrl,
  normalizeMediaImageKey,
  prefetchMediaObjectUrl,
  resolveMediaObjectUrl,
} from "Helpers/mediaImageCache";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";

type InspectionImagesCarouselModalProps = {
  open: boolean;
  onClose: () => void;
  /** Raw image names/URLs for the selected item. */
  images: string[];
  title?: string;
};

/** Fixed modal body height so the dialog does not jump when images load. */
const MODAL_BODY_HEIGHT = 420;
const IMAGE_VIEWPORT_HEIGHT = 360;

const uniqueImageKeys = (images: string[]) => {
  const seen = new Set<string>();
  const keys: string[] = [];
  (images || []).forEach((raw) => {
    const key = normalizeMediaImageKey(raw);
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  });
  return keys;
};

export default function InspectionImagesCarouselModal({
  open,
  onClose,
  images,
  title = "viewImages",
}: InspectionImagesCarouselModalProps) {
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  const imageKeys = useMemo(() => uniqueImageKeys(images), [images]);
  const imagesId = useMemo(() => imageKeys.join("\0"), [imageKeys]);

  const [index, setIndex] = useState(0);
  const [imagesIdState, setImagesIdState] = useState(imagesId);
  // Reset index synchronously when switching items / image sets.
  if (imagesId !== imagesIdState) {
    setImagesIdState(imagesId);
    setIndex(0);
  }

  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  /** Bumps when modal item/index changes so stale responses are ignored. */
  const loadGen = useRef(0);

  const safeIndex =
    imageKeys.length === 0
      ? 0
      : Math.min(Math.max(index, 0), imageKeys.length - 1);
  const isFirst = safeIndex <= 0;
  const isLast = safeIndex >= imageKeys.length - 1;

  const loadImageAt = useCallback(async (key: string, gen: number) => {
    if (!key) {
      if (gen === loadGen.current) {
        setSrc("");
        setLoading(false);
        setError(true);
      }
      return;
    }

    const cached = getCachedMediaObjectUrl(key);
    if (cached) {
      if (gen === loadGen.current) {
        setSrc(cached);
        setLoading(false);
        setError(false);
      }
      return;
    }

    if (gen === loadGen.current) {
      // Clear previous item's image immediately while loading.
      setSrc("");
      setLoading(true);
      setError(false);
    }

    try {
      const url = await resolveMediaObjectUrl(key);
      if (gen !== loadGen.current) return;
      if (url) {
        setSrc(url);
        setError(false);
      } else {
        setSrc("");
        setError(true);
      }
    } catch {
      if (gen !== loadGen.current) return;
      setSrc("");
      setError(true);
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      loadGen.current += 1;
      setSrc("");
      setLoading(false);
      setError(false);
      return;
    }
    const gen = ++loadGen.current;
    const key = imageKeys[safeIndex] || "";
    void loadImageAt(key, gen);
  }, [open, imagesId, safeIndex, imageKeys, loadImageAt]);

  // Prefetch next after current succeeds.
  useEffect(() => {
    if (!open || loading || error || !src || isLast) return;
    const nextKey = imageKeys[safeIndex + 1];
    if (nextKey) prefetchMediaObjectUrl(nextKey);
  }, [open, loading, error, src, isLast, imageKeys, safeIndex]);

  const goPrev = () => {
    if (isFirst) return;
    setIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (isLast) return;
    setIndex((i) => Math.min(imageKeys.length - 1, i + 1));
  };

  const handleClose = (_event?: any, reason?: any) => {
    if (reason === "backdropClick") return;
    loadGen.current += 1;
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={title}
      hideActions
      fullWidth
      maxWidth="md"
      content={
        <Box
          className="flex flex-col items-center gap-3 py-2 w-full"
          sx={{
            height: MODAL_BODY_HEIGHT,
            minHeight: MODAL_BODY_HEIGHT,
            maxHeight: MODAL_BODY_HEIGHT,
            boxSizing: "border-box",
          }}
        >
          {!imageKeys.length ? (
            <Box
              className="flex items-center justify-center w-full flex-1"
              sx={{ height: IMAGE_VIEWPORT_HEIGHT }}
            >
              <Typography className="font-Saira text-[13px] text-txt-color">
                {t("noImagesAvailable")}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                className="relative w-full flex items-center justify-center shrink-0"
                sx={{
                  height: IMAGE_VIEWPORT_HEIGHT,
                  minHeight: IMAGE_VIEWPORT_HEIGHT,
                  maxHeight: IMAGE_VIEWPORT_HEIGHT,
                  background: "#f7f7f7",
                  borderRadius: 2,
                  border: "1px solid #eee",
                  overflow: "hidden",
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={36}
                    sx={{ color: themeColors?.primary }}
                  />
                ) : null}
                {!loading && error ? (
                  <Typography className="font-Saira text-[13px] text-txt-color px-4 text-center">
                    {t("imageLoadFailed")}
                  </Typography>
                ) : null}
                {!loading && !error && src ? (
                  <Box
                    component="img"
                    src={src}
                    alt={t("inspection")}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : null}
              </Box>

              <Box className="flex items-center justify-center gap-4 w-full shrink-0">
                <IconButton
                  aria-label={t("previous") || "Previous"}
                  onClick={goPrev}
                  disabled={isFirst}
                  sx={{
                    color: themeColors?.primary,
                    border: "1px solid #eee",
                    "&.Mui-disabled": { opacity: 0.35 },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography className="font-Saira font-[600] text-[13px] min-w-[64px] text-center">
                  {imageKeys.length ? `${safeIndex + 1} / ${imageKeys.length}` : "0 / 0"}
                </Typography>
                <IconButton
                  aria-label={t("next") || "Next"}
                  onClick={goNext}
                  disabled={isLast}
                  sx={{
                    color: themeColors?.primary,
                    border: "1px solid #eee",
                    "&.Mui-disabled": { opacity: 0.35 },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      }
    />
  );
}
